/**
 * Pane → PanePlan: the declarative render plan the SciChart shell consumes.
 *
 * This is the pure "brain" of the ChartBuilder — it turns a contract `Pane`
 * (plus the active theme) into fully-resolved axis and series descriptions with
 * concrete colors, so the imperative SciChart layer is a thin, mechanical
 * translation with no model logic of its own. Being pure, it is fully unit
 * testable without a browser or WASM.
 */

import {
  resolveColorRef,
  DEFAULT_THEME,
  type Pane,
  type Plot,
  type Theme,
} from "@qufolio/model";
import {
  DEFAULT_GRID_LINES,
  gridDashFor,
  resolveAxisPlan,
  type AxisGridPlan,
  type AxisPlan,
  type GridLineStyle,
} from "./axis.js";
import { resolveSeriesType, type SeriesKind, type DataKind } from "./series.js";
import { resolvePalettePlan, type PalettePlan } from "./palette.js";
import { plotDashFor, resolvePlotDash, type DashStyle } from "./dash.js";
import {
  assignSignalLanes,
  DEFAULT_EVENT_KIND,
  DEFAULT_MARKER_SHAPE,
  type EventKind,
  type MarkerShape,
  type SignalLanePlan,
} from "./signal.js";

export interface SeriesPlan {
  plotId: string;
  order: number;
  /**
   * The contract type this came from, kept alongside the resolved kinds.
   *
   * `seriesKind` is lossy on purpose — several plot types collapse onto one
   * renderable — and that is right for the shell, which only wants to know what
   * to construct. It is wrong for anyone asking *what is this*: `volumeByPrice`
   * maps to `"column"` for want of an honest answer, so a rule written against
   * `seriesKind` would catch every real column with it.
   */
  plotType: Plot["type"];
  seriesKind: SeriesKind;
  dataKind: DataKind;
  indicatorRef?: string;
  outputAlias?: string;
  axisRef?: string;
  /**
   * Drawn against a signed log axis, whose values are growth fractions — so
   * the value legend reads them as percentages, as the axis labels do.
   */
  signedLog?: boolean;
  hidden: boolean;
  /** Resolved concrete colors (tokens already applied). */
  stroke?: string;
  fill?: string;
  opacity?: number;
  thickness?: number;
  /** Already resolved: an explicit `dash` beats the named `dashStyle`. */
  dash?: number[];
  /**
   * Draw as a step rather than interpolating between points.
   *
   * Only the stroked-line family can — line, mountain and band — which is the
   * same set that can be dashed, and for the same reason: SciChart hangs both
   * properties off that base and nowhere else.
   */
  digital: boolean;
  /**
   * The glyph a `signal` plot draws. Always resolved — `circle` when the plot
   * says nothing — so the shell never has to know the default. Ignored by every
   * other plot type.
   */
  markerShape: MarkerShape;
  /** Marker size in px, or absent for the renderer's own default by type. */
  markerSize?: number;
  /** What a `text` marker reads (`PlotStyle.markerText`); absent for every other shape. */
  markerText?: string;
  palette: PalettePlan;
  /**
   * How a candlestick colours itself — `PlotPalette.candleStyle`, resolved:
   * absent is `standard` here, since the viewer's default (hollow for a JAS
   * member) is the host's to fill in before the plan is built. Ignored by every
   * other series kind. See `plan/candle-colors.ts`.
   */
  candleStyle: "standard" | "hollow";
  /**
   * Present only for `type: "signal"` — the lane this plot's markers occupy and
   * what they hang off.
   *
   * Resolved here, with the rest of the plan, because it is a decision and not a
   * drawing: which lane depends on the other plots in the pane, which is exactly
   * the kind of thing the shell should be handed rather than work out.
   */
  signal?: SignalLanePlan;
  /**
   * How a signal's values read — `single` or `dual`, from the catalog through
   * {@link EventKindLookup}. Always resolved (`single` when the catalog says
   * nothing or has not arrived); meaningless on every other plot type.
   */
  eventKind: EventKind;
}

/**
 * Look up how one signal output reads, by the plot's `indicatorRef` (the family)
 * and `outputAlias`. Injected for {@link AxisSpecLookup}'s reason: this layer
 * cannot fetch, and with no catalog every signal reads as `single` — today's
 * behaviour — rather than as a guess.
 */
export type EventKindLookup = (
  indicatorRef: string,
  outputAlias: string,
) => EventKind | undefined;

export interface PanePlan {
  paneId: string;
  role: Pane["role"];
  /** X-axis labels render only on the price pane (derived from role, never stored). */
  showXLabels: boolean;
  axes: AxisPlan[];
  /**
   * The vertical gridlines, from the X axis.
   *
   * Resolved here rather than left to the shell because the X axis has no
   * `AxisInfo` in the contract — a chart group declares only its *type* — so
   * there is nothing to hang a per-axis override on. It follows the group
   * setting exactly, which is also what makes "dotted" mean both directions.
   */
  xGrid: AxisGridPlan;
  series: SeriesPlan[];
  /**
   * How many signal lanes this pane carries; `0` when it has no signals.
   *
   * The shell needs it before it builds any series, because the lane axis has a
   * range fixed by the *count* — an axis fitted to its data would move every
   * lane the first time a signal fired.
   */
  signalLanes: number;
  /**
   * Reference lines: the user's own where an axis carries them, else the ones
   * the indicator declares — RSI's 30/70, MACD's zero.
   *
   * Empty when no catalog was injected *and* no axis stores levels, which is the
   * honest degradation: a host with nothing to look a convention up in gets no
   * lines rather than guessed ones. A stored level needs no catalog, because it
   * is already in the pane.
   */
  referenceLines: ReferenceLinePlan[];
}

/**
 * One reference line — RSI's 30, MACD's zero, or a level the user placed.
 *
 * A *plan* artifact either way, assembled from two sources that never mix on one
 * axis. The catalog says RSI is banded at 30 and 70, and that is synthesised
 * each build so a convention corrected server-side reaches every existing chart
 * and no workspace grows rows for it (D8) — writing real `hline` annotations on
 * add would make every level a snapshot correctable only by hand. A user's own
 * level is the opposite case: it is not derivable from anything, so it is stored
 * on `AxisInfo.levels` and read straight out of the pane.
 */
export interface ReferenceLinePlan {
  axisRef?: string;
  value: number;
  label?: string;
  dash: number[] | undefined;
  stroke: string;
  /**
   * Shade the axis's first line past this level (`AxisLevel.fill`) — RSI's 70
   * above, 30 below. `color` resolved, `opacity` defaulted; the shell draws it
   * as a band under the line, so it meets the line at the crossing.
   */
  fill?: { side: "above" | "below"; color: string; opacity: number };
}

/** A level's fill as stored or declared: side, and an optional colour and opacity. */
export type LevelFillSpec = { side: "above" | "below"; color?: string; opacity?: number };

/** What an indicator declares about the axis it is drawn against. */
export interface IndicatorAxisSpec {
  zeroLine?: boolean;
  levels?: { value: number; label?: string; dash?: string; fill?: LevelFillSpec }[];
}

/** Opacity of a level fill that states none (owner's mock, 2026-11-02). */
export const LEVEL_FILL_OPACITY = 0.3;

/**
 * A fill resolved for drawing: `positive` above and `negative` below unless it
 * names a colour, 0.3 unless it names an opacity. Tokens, so a theme switch
 * recolours it (D13).
 */
function levelFill(fill: LevelFillSpec | undefined, theme: Theme): ReferenceLinePlan["fill"] {
  if (!fill || (fill.side !== "above" && fill.side !== "below")) return undefined;
  const token = fill.color ?? (fill.side === "above" ? "positive" : "negative");
  const color = resolveColorRef(token, theme);
  if (!color) return undefined;
  return { side: fill.side, color, opacity: fill.opacity ?? LEVEL_FILL_OPACITY };
}

/**
 * Look up an indicator's axis declaration by the name `Plot.indicatorRef` holds.
 *
 * Injected rather than imported: the plan layer cannot fetch, and a host with no
 * catalog gets **no** reference lines rather than wrong ones — the same way
 * `screener-grid` degrades with no injected DSL catalog.
 */
export type AxisSpecLookup = (indicatorRef: string) => IndicatorAxisSpec | undefined;

/**
 * The id of the axis a plot is actually drawn against.
 *
 * Its `axisRef` when that names an axis this pane declares, and the pane's first
 * axis otherwise — which covers both a plot carrying no ref and one carrying a
 * ref that no longer resolves. Returns `""` for a pane with no axes at all, a
 * value no axis id can equal, so a lookup against it simply misses.
 *
 * Deliberately not exported: this is the reference-line path's reading of a
 * relationship the renderer resolves its own way. `scichart-builder` passes
 * `yAxisId` straight through and lets a non-matching id draw nothing, which is
 * the right behaviour for a *series* — a plot bound to a missing axis has no
 * scale and cannot be drawn anywhere honest. A reference line is the opposite
 * case: it is the pane's own furniture, so the question "is this pane's axis
 * overridden" has an answer even when a plot's bookkeeping is stale.
 */
function axisOf(pane: Pane, plot: { axisRef?: string }): string {
  const axes = pane.axes ?? [];
  if (plot.axisRef && axes.some((axis) => axis.id === plot.axisRef)) return plot.axisRef;
  return axes[0]?.id ?? "";
}

function referenceLines(pane: Pane, theme: Theme, lookup?: AxisSpecLookup): ReferenceLinePlan[] {
  // `text.muted`, not `axis.line` (owner, 2026-11-02): in the axis colour, and
  // dotted, RSI's 30/70 could not be found. Quiet, but text-strength.
  const stroke =
    resolveColorRef("text.muted", theme) ??
    resolveColorRef("axis.line", theme) ??
    resolveColorRef("border", theme) ??
    "#888888";
  const lines: ReferenceLinePlan[] = [];

  // The user's own levels, and the axes they take over.
  //
  // `AxisInfo.levels` absent means the catalog's; present means *exactly* these,
  // and `[]` therefore means "I deleted them all" rather than "I never touched
  // it" — which is why this tests the array's presence and never its length.
  //
  // The dedupe key here is the **axis id**, not the `indicatorRef` the catalog
  // path below keys on, and the two differ exactly when a pane holds two
  // families against one axis. The catalog owes each family its own levels; an
  // axis owes the user one set however many families are drawn against it, so
  // RSI and CCI sharing an axis must not each draw it. Iterating the axes rather
  // than carrying a second `Set` over the plots makes that structural: an axis
  // appears once in `pane.axes`, so it cannot be drawn twice, and an override
  // still reaches an axis whose plots have not loaded yet.
  const overridden = new Set<string>();
  for (const axis of pane.axes ?? []) {
    if (!axis.levels) continue;
    overridden.add(axis.id);
    for (const level of axis.levels) {
      lines.push({
        axisRef: axis.id,
        value: level.value,
        label: level.label,
        dash: plotDashFor(level.dash),
        // A token or hex resolved here, like every other colour in the plan, so
        // the line follows a theme switch (D13); absent falls back to the same
        // axis-line colour a catalog level draws with.
        stroke: resolveColorRef(level.color, theme) ?? stroke,
        ...(level.fill ? { fill: levelFill(level.fill, theme) } : {}),
      });
    }
  }

  // Only the *catalog* needs injecting — a stored level is already in the pane,
  // so a host with no catalog still draws the user's lines and loses only the
  // conventions it has nothing to look up.
  if (!lookup) return lines;
  const seen = new Set<string>();

  for (const plot of pane.plots ?? []) {
    const ref = plot.indicatorRef;
    if (!ref) continue;
    // An axis carrying its own levels answers for every plot drawn against it,
    // including the family's `zeroLine`: CCI's 0 is one of the lines the user
    // lists, and leaving the catalog's underneath a user's set would draw a line
    // they can see and cannot delete. Checked *before* `seen`, so an overridden
    // plot does not consume its family's one slot — the same family drawn again
    // on a second, un-overridden axis in this pane still gets the catalog's.
    //
    // **Which axis a plot is on cannot be read off `axisRef` alone**, and
    // testing it directly is a double-draw waiting to happen: two shipped paths
    // produce a plot whose ref does not resolve. `layout-adopt.ts` *deletes*
    // `axisRef` when a repoint fails, and `chart-template.ts` mints a fresh id
    // for every axis without repointing the plots at all — so a templated CCI
    // pane has an overridden axis that none of its plots name, and it would
    // emit the user's four levels *and* the catalog's three underneath.
    //
    // `paneAxis` and `addIndicator` already answer this the same way, in the
    // same words: a pane may declare several axes, an indicator pane in practice
    // has one, so a plot that does not name a live one belongs to the first.
    if (overridden.has(axisOf(pane, plot))) continue;
    // One family per pane is the normal case, but a pane holding two would
    // otherwise draw each family's levels once per output.
    if (seen.has(ref)) continue;
    seen.add(ref);
    const spec = lookup(ref);
    if (!spec) continue;

    if (spec.zeroLine) {
      lines.push({ axisRef: plot.axisRef, value: 0, dash: undefined, stroke });
    }
    for (const level of spec.levels ?? []) {
      lines.push({
        axisRef: plot.axisRef,
        value: level.value,
        label: level.label,
        dash: plotDashFor(level.dash as DashStyle | undefined),
        stroke,
        ...(level.fill ? { fill: levelFill(level.fill, theme) } : {}),
      });
    }
  }
  return lines;
}

export function buildPanePlan(
  pane: Pane,
  theme: Theme = DEFAULT_THEME,
  gridLines: GridLineStyle = DEFAULT_GRID_LINES,
  axisSpecs?: AxisSpecLookup,
  eventKinds?: EventKindLookup,
): PanePlan {
  const axes = (pane.axes ?? []).map((a) => resolveAxisPlan(a, theme, gridLines));

  // Lanes come from the pane's *whole* plot list, before it is sorted or
  // filtered here — a signal's lane is its position among the pane's signals,
  // which is not a property of the plot on its own.
  const lanes = new Map(
    assignSignalLanes(pane.plots ?? []).map((lane) => [lane.plotId, lane] as const),
  );

  const series: SeriesPlan[] = (pane.plots ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((plot) => {
      const { seriesKind, dataKind } = resolveSeriesType(plot.type);
      const style = plot.style ?? {};
      return {
        plotId: plot.id,
        order: plot.order ?? 0,
        plotType: plot.type,
        seriesKind,
        dataKind,
        indicatorRef: plot.indicatorRef,
        outputAlias: plot.outputAlias,
        axisRef: plot.axisRef,
        ...(axes.find((a) => a.id === axisOf(pane, plot))?.signedLog ? { signedLog: true } : {}),
        hidden: plot.hidden ?? false,
        stroke: resolveColorRef(style.stroke, theme),
        fill: resolveColorRef(style.fill, theme),
        opacity: style.opacity,
        thickness: style.thickness,
        dash: resolvePlotDash(style),
        digital: style.digital ?? false,
        markerShape: style.markerShape ?? DEFAULT_MARKER_SHAPE,
        markerSize: style.markerSize,
        ...(style.markerText ? { markerText: style.markerText } : {}),
        palette: resolvePalettePlan(plot.palette, theme),
        candleStyle: plot.palette?.candleStyle ?? "standard",
        signal: lanes.get(plot.id),
        eventKind:
          (plot.type === "signal" && plot.indicatorRef && plot.outputAlias
            ? eventKinds?.(plot.indicatorRef, plot.outputAlias)
            : undefined) ?? DEFAULT_EVENT_KIND,
      };
    });

  return {
    paneId: pane.id,
    role: pane.role,
    showXLabels: pane.role === "price",
    axes,
    xGrid: {
      draw: gridLines !== "none",
      majorDash: gridDashFor(gridLines),
      // Colour and thickness are the theme's (D13); only the pattern is set here.
      minorsPerMajor: 2,
    },
    series,
    // The highest lane, not the number of signal plots: plots sharing a `row`
    // share a lane, so counting plots would leave empty rows at the far end.
    signalLanes: Math.max(0, ...[...lanes.values()].map((plan) => plan.lane)),
    referenceLines: referenceLines(pane, theme, axisSpecs),
  };
}